/**
 * FctPhaseProgressBar — barra de progreso con etiqueta + contadores.
 *
 * Patrón GCvsc §7.3 (PhaseProgress): barras de fase para procesos largos
 * donde cada fase tiene un nombre, un porcentaje, conteos (procesados / total
 * / errores) y un estado discreto (no iniciada / activa / completada).
 *
 * Estado visual de la barra:
 *  - `notStarted` → gris (Progress normal)
 *  - `active`     → azul animado (status="active")
 *  - `completed`  → verde (status="success")
 *  - Si hay errores y la fase está activa, el stroke se pinta en naranja
 *    (gold-6 según convención GCvsc).
 *
 * Uso típico (dos fases secuenciales A → B):
 *
 *   <FctPhaseProgressBar
 *     label="Fase A — Sincronizar contactos elegibles"
 *     procesados={2350}
 *     total={9100}
 *     errores={3}
 *     status={faseA === 'active' ? 'active' : (faseA === 'completed' ? 'completed' : 'notStarted')}
 *   />
 *   <FctPhaseProgressBar
 *     label="Fase B — Validar con ZeroBounce"
 *     procesados={0}
 *     total={9100}
 *     errores={0}
 *     status="notStarted"
 *   />
 */

import React from 'react';
import { Progress, Typography } from 'antd';
import { fctColors } from '../../theme/theme';

const { Text } = Typography;

export type FctPhaseStatus = 'notStarted' | 'active' | 'completed';

interface FctPhaseProgressBarProps {
  /** Etiqueta de la fase. */
  label: string;
  procesados: number;
  total: number;
  errores?: number;
  status: FctPhaseStatus;
  /** Margen inferior. Por defecto 12. */
  marginBottom?: number;
}

export const FctPhaseProgressBar: React.FC<FctPhaseProgressBarProps> = ({
  label,
  procesados,
  total,
  errores = 0,
  status,
  marginBottom = 12,
}) => {
  const percent =
    total > 0 ? Math.min(100, Math.round((procesados / total) * 100)) : 0;

  // Mapeo de status del componente a status de Progress de AntD
  const progressStatus: 'active' | 'success' | 'normal' =
    status === 'completed'
      ? 'success'
      : status === 'active'
        ? 'active'
        : 'normal';

  // Si hay errores en una fase activa, tintamos la barra de naranja
  const strokeColor =
    errores > 0 && status === 'active' ? fctColors.faseErrorStroke : undefined;

  return (
    <div style={{ marginBottom }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 4,
        }}
      >
        <Text strong style={{ fontSize: 13 }}>
          {label}
        </Text>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {procesados} / {total}
          {errores > 0 && ` · ${errores} errores`}
        </Text>
      </div>
      <Progress
        percent={percent}
        status={progressStatus}
        size="small"
        showInfo={false}
        strokeColor={strokeColor}
      />
    </div>
  );
};
